/**
 * regressionAndRestorationDetector.js
 *
 * Detects whether a person is currently in regression or restoration,
 * and produces adjustments to Kevia's response posture accordingly.
 *
 * IMPORTANT: NOT condenatory. Regression is detected to INCREASE compassion,
 * not to increase correction.
 *
 * Detection types:
 *   regression             — clear step backward from a better state
 *   recovery               — beginning to emerge from a difficult state
 *   sustained_restoration  — multiple consecutive recovery signals
 *   sustained_regression   — multiple consecutive crisis signals
 *   oscillation            — alternating regression/recovery without resolution
 *   stable                 — no significant movement detected
 */

import { getRecentEntries } from "./spiritualJourneyMemory";

const CRISIS_STATES   = new Set(["quebranto", "resistencia", "confusión", "agotamiento_espiritual"]);
const RECOVERY_STATES = new Set(["restauración", "apertura", "crecimiento"]);

/**
 * @param {import("./spiritualJourneyMemory").JourneyRecord|null} record
 * @param {string} currentState — from FASE 8 spiritualProcessDiscernment
 * @returns {{
 *   type:       "regression"|"recovery"|"sustained_restoration"|"sustained_regression"|"oscillation"|"stable",
 *   adjustments: {
 *     increasePatience:       boolean,
 *     reduceCorrection:       boolean,
 *     increaseAccompaniment:  boolean,
 *     acknowledgeProgress:    boolean,
 *   },
 *   instruction: string|null,
 * }}
 */
export function detectRegressionRestoration(record, currentState) {
  const stable = {
    type: "stable",
    adjustments: {
      increasePatience: false,
      reduceCorrection: false,
      increaseAccompaniment: false,
      acknowledgeProgress: false,
    },
    instruction: null,
  };

  if (!record?.entries?.length || record.entries.length < 2) return stable;

  const recent = getRecentEntries(record, { limit: 5 });
  const states = recent.map((e) => e.processState);
  const prev   = states[1] ?? null;

  // ── Sustained restoration (3+ consecutive recovery signals) ──────────────
  const consecutiveRecovery = _consecutiveCount(states, RECOVERY_STATES);
  if (consecutiveRecovery >= 3) {
    return {
      type: "sustained_restoration",
      adjustments: {
        increasePatience: false,
        reduceCorrection: true,
        increaseAccompaniment: false,
        acknowledgeProgress: true,
      },
      instruction:
        "Proceso de restauración sostenido. Afirma el camino con calidez sin exagerar ni presionar.",
    };
  }

  // ── Sustained regression (3+ consecutive crisis signals) ─────────────────
  const consecutiveCrisis = _consecutiveCount(states, CRISIS_STATES);
  if (consecutiveCrisis >= 3) {
    return {
      type: "sustained_regression",
      adjustments: {
        increasePatience: true,
        reduceCorrection: true,
        increaseAccompaniment: true,
        acknowledgeProgress: false,
      },
      instruction:
        "Situación difícil prolongada. Aumenta paciencia y presencia. No exhortes mientras no hay terreno firme.",
    };
  }

  // ── Regression: currently in crisis after recovery ────────────────────────
  if (CRISIS_STATES.has(currentState) && prev !== null && RECOVERY_STATES.has(prev)) {
    return {
      type: "regression",
      adjustments: {
        increasePatience: true,
        reduceCorrection: true,
        increaseAccompaniment: true,
        acknowledgeProgress: false,
      },
      instruction:
        "Hay un retroceso después de avance. No señales el retroceso directamente — acompaña con más paciencia.",
    };
  }

  // ── Recovery: currently recovering after crisis ───────────────────────────
  if (RECOVERY_STATES.has(currentState) && prev !== null && CRISIS_STATES.has(prev)) {
    return {
      type: "recovery",
      adjustments: {
        increasePatience: false,
        reduceCorrection: true,
        increaseAccompaniment: false,
        acknowledgeProgress: true,
      },
      instruction:
        "Hay señales de recuperación. Sostén el momentum sin presionar — el proceso es frágil.",
    };
  }

  // ── Oscillation: alternating states without resolution ───────────────────
  const hasBothInRecent = states.some((s) => CRISIS_STATES.has(s)) &&
                          states.some((s) => RECOVERY_STATES.has(s));
  if (hasBothInRecent && states.length >= 4) {
    return {
      type: "oscillation",
      adjustments: {
        increasePatience: true,
        reduceCorrection: true,
        increaseAccompaniment: true,
        acknowledgeProgress: false,
      },
      instruction:
        "Estado espiritual oscilante. No anticipes resolución. Acompaña con constancia.",
    };
  }

  return stable;
}

// ── Private ───────────────────────────────────────────────────────────────────

function _consecutiveCount(states, stateSet) {
  let count = 0;
  for (const s of states) {
    if (stateSet.has(s)) count++;
    else break;
  }
  return count;
}
